import * as _ from 'underscore';

import * as Globalization from '../../../_common/platform/ts/Globalization';
import * as Utils from '../../../_common/platform/ts/Utilities';
import { NullHandling, nullHandlingBehaviour } from '../../../_common/platform/ts/nullHandling';
import { getExpressionType, IExpression } from '../../../pdc-platform/src/ts/stores/EntityModelsStore';
import * as DateTimeFunctions from '../../../_common/platform/ts/DateTimeFunctions';
import { DevExpressDataType } from '../../platform/ts/Interfaces';
import { themeVars } from '../../../pdc-platform/src/ts/themes/themeConstants';
import { IValuesCollection } from './appTheme';

import { ATTRIBUTE_TYPES, TIME_PART_FORMATS, TIME_PART_TYPES } from '../../platform/ts/ConfigurationConstants';

export interface IValueFormat {
    precision?: number;
    formatFront?: string;
    currency?: string;
    customFormat?: string;
    useDigitGrouping?: boolean;
    overwriteInheritedUseDigitGrouping?: boolean;
    formatZeroAsBlank?: boolean;
    overwriteInheritedFormatZeroAsBlank?: boolean;
    use24hours?: boolean;
    firstDayOfWeek?: string;
    fiscalFirstMonth?: number;
    useFiscalPrefix?: boolean;
    fiscalYearDivider?: string;
    formatAsType?: string;
    mapping?;
    dataType?;
}

export const LATIN_NUMBERS = {
    '1': 'I',
    '2': 'II',
    '3': 'III',
    '4': 'IV'
};

//LL: 02.10.18: for "half year"
const ORDINAL_NUMBERS = {
    '1': '1st',
    '2': '2nd'
};

export type IOnConverionErrorCallback = (errorText: string) => void;

export function getValueFormatFromTheme(
    attributeType: string,
    theme: IValuesCollection,
    timePart?: string
): IValueFormat {
    if (theme == null) {
        return null;
    }

    if (timePart != null && DateTimeFunctions.isTypeOfAnyDate(attributeType)) {
        switch (timePart) {
            case TIME_PART_TYPES.FISCAL_YEAR:
                return theme[themeVars.formats_fiscalYear];

            default:
                return null;
        }
    } else {
        switch (attributeType) {
            case ATTRIBUTE_TYPES.DATE:
                return theme[themeVars.formats_date];

            case ATTRIBUTE_TYPES.DATETIME:
                return theme[themeVars.formats_datetime];

            case ATTRIBUTE_TYPES.TIME:
                return theme[themeVars.formats_time];

            case ATTRIBUTE_TYPES.INTEGER:
                return theme[themeVars.formats_integer];

            case ATTRIBUTE_TYPES.REAL:
                return theme[themeVars.formats_real];

            case ATTRIBUTE_TYPES.BOOLEAN:
                return theme[themeVars.formats_boolean];

            default:
                return null;
        }
    }
}

//EVG 12.09.22: This function is to calculate default value for useDigitGrouping.
export function getActualUseDigitGrouping(useDigitGrouping: boolean) {
    return useDigitGrouping === true; //False by default.
}

export function getValueFormatsFromTheme<TValue extends IValueFormat>(
    attributeType: string,
    theme: IValuesCollection,
    value?: TValue
) {
    let result: TValue;

    if (value != null) {
        result = { ...value };

        if (result.formatFront === 'default' || result.formatFront === '') {
            delete result.formatFront;
        }
    } else {
        result = {} as TValue;
    }

    if (theme == null) {
        return result;
    }

    const themeValueFormat = getValueFormatFromTheme(attributeType, theme);

    if (themeValueFormat != null) {
        if (result.formatFront == null && themeValueFormat.formatFront != null) {
            result.formatFront = themeValueFormat.formatFront;
        }

        if (result.customFormat == null && themeValueFormat.customFormat != null) {
            result.customFormat = themeValueFormat.customFormat;
        }
    }

    result.fiscalFirstMonth = Globalization.getCultureValues(theme).fiscalFirstMonth;

    if (DateTimeFunctions.isTypeOfAnyDate(attributeType)) {
        if (result.firstDayOfWeek == null) {
            result.firstDayOfWeek = Globalization.getFirstWeekDay(theme);
        }

        if (attributeType !== ATTRIBUTE_TYPES.DATE) {
            const use24hours = theme[themeVars.use24HoursFormat];

            if (use24hours) {
                result.use24hours = use24hours;
            }
        }
    } else if (attributeType === ATTRIBUTE_TYPES.INTEGER || attributeType === ATTRIBUTE_TYPES.REAL) {
        if (themeValueFormat != null) {
            if (result.currency == null && themeValueFormat.currency != null) {
                result.currency = themeValueFormat.currency;
            }

            if (result.precision == null && themeValueFormat.precision != null) {
                result.precision = themeValueFormat.precision;
            }

            if (result.useDigitGrouping == null) {
                result.useDigitGrouping = themeValueFormat.useDigitGrouping;
            }

            if (result.formatZeroAsBlank == null) {
                result.formatZeroAsBlank = themeValueFormat.formatZeroAsBlank;
            }
        }

        // In DevXpress it is true by default, so it must be set false explicitly.
        result.useDigitGrouping = getActualUseDigitGrouping(result.useDigitGrouping);

        if (result.currency == null) {
            result.currency = theme[themeVars.currencySelector];
        }

        if (result.firstDayOfWeek == null && attributeType === ATTRIBUTE_TYPES.INTEGER) {
            // Is used for Time Parts.
            result.firstDayOfWeek = Globalization.getFirstWeekDay(theme);
        }
    }

    return result;
}

function convertDate(
    value,
    timePart: string,
    format: string,
    dataType: string,
    onConverionErrorCallback: IOnConverionErrorCallback,
    customFormat?: string,
    use24hours?: boolean,
    firstDayOfWeek?: string,
    firstFiscalMonth?: number,
    useFiscalPrefix?: boolean,
    fiscalYearDivider?: string
): string {
    //  when timePart: formatting needed for weekday (1-7).
    // Maybe also for quarter (1 - 4), week(1 - 53): add Q or W

    if (value == null) {
        return '';
    }

    if (use24hours == null) {
        use24hours = false;
    }

    const isDateValue = Globalization.isDate(value);

    let isDefaultFormat = format == null || format === '' || format === 'default';
    let tmpValue = value;

    if (!isDateValue) {
        if (dataType === ATTRIBUTE_TYPES.TIME) {
            //LL: 26.08.18: value can be here of type Date, not only String (when from Charts):
            tmpValue = '1970-01-01 ' + value;
        } else if (dataType === ATTRIBUTE_TYPES.DATE) {
            if (typeof value === 'string' && value.indexOf('T') < 0 && value.indexOf('00:00') < 0) {
                //EVG: See Issue 85735. When a date is without time part,
                // the zero time part must be added to make Globalize format it without changing Time Zone.
                tmpValue += ' 00:00:00';
            }
        }
    }

    if (isDefaultFormat) {
        switch (dataType) {
            case ATTRIBUTE_TYPES.DATE:
                format = 'longDate';
                isDefaultFormat = false;
                break;

            case ATTRIBUTE_TYPES.TIME:
                format = 'longTime';
                isDefaultFormat = false;
                break;
        }
    }

    let result = value.toString();

    //This is wrong when value is a date in string format:
    // if (typeof value === 'string') {
    //     n = parseInt(value, 10);   // if (!isNaN(n))
    // }

    let actualFormat: string;
    let pattern: any; //may be string or object

    switch (timePart) {
        case TIME_PART_TYPES.YEAR:
            actualFormat = isDefaultFormat ? TIME_PART_FORMATS.FOUR_DIGITS : format;

            switch (actualFormat) {
                case TIME_PART_FORMATS.FOUR_DIGITS:
                    result = Globalization.formatDate(tmpValue, { raw: 'yyyy' });
                    break;
                case TIME_PART_FORMATS.TWO_DIGITS:
                    result = Globalization.formatDate(tmpValue, { raw: 'yy' });
                    break;
            }

            break;

        //LL: 17.10.18: also used 'longYear' and 'shortYear' above, defult is actually '4digits' above
        case TIME_PART_TYPES.FISCAL_YEAR:
            actualFormat = isDefaultFormat ? TIME_PART_FORMATS.FOUR_DIGITS : format;

            switch (actualFormat) {
                case TIME_PART_FORMATS.FOUR_DIGITS:
                    result = Globalization.formatDate(tmpValue, { raw: 'yyyy' });
                    break;
                case TIME_PART_FORMATS.TWO_DIGITS:
                    result = Globalization.formatDate(tmpValue, { raw: 'yy' });
                    break;
                case TIME_PART_FORMATS.LONG_FISCAL_YEAR:
                    result = 'FY' + Globalization.formatDate(tmpValue, { raw: 'yyyy' });
                    break;
                case TIME_PART_FORMATS.SHORT_FISCAL_YEAR:
                    result = 'FY' + Globalization.formatDate(tmpValue, { raw: 'yy' });
                    break;
                case TIME_PART_FORMATS.SHORT_FISCAL_YEAR_START_END:
                    if (firstFiscalMonth === 1) {
                        result = Globalization.formatDate(tmpValue, { raw: 'yy' });
                        result = (useFiscalPrefix === false ? '' : 'FY') + result;
                    } else {
                        const nextYear = moment(tmpValue).add(1, 'y');

                        result =
                            Globalization.formatDate(tmpValue, { raw: 'yy' }) +
                            (fiscalYearDivider || '-') +
                            Globalization.formatDate(nextYear, { raw: 'yy' });

                        if (actualFormat === TIME_PART_FORMATS.SHORT_FISCAL_YEAR_START_END) {
                            result = (useFiscalPrefix === false ? '' : 'FY') + result;
                        }
                    }
                    break;

                case TIME_PART_FORMATS.LONG_FISCAL_YEAR_START_END:
                    if (firstFiscalMonth === 1) {
                        result = Globalization.formatDate(tmpValue, { raw: 'yyyy' });
                        result = (useFiscalPrefix === false ? '' : 'FY') + result;
                    } else {
                        const nextYear = moment(tmpValue).add(1, 'y');

                        result =
                            (useFiscalPrefix === false ? '' : 'FY') +
                            Globalization.formatDate(tmpValue, { raw: 'yyyy' }) +
                            (fiscalYearDivider || '-') +
                            Globalization.formatDate(nextYear, { raw: 'yyyy' });
                    }
                    break;
            }

            break;

        //LL: 17.10.18: And also TimePart "halfYearFiscal"
        //LL: 02.10.18  value = 1 or 2
        case TIME_PART_TYPES.HALF_YEAR:
            actualFormat = isDefaultFormat ? 'default' : format;

            switch (actualFormat) {
                case 'default':
                    break;
                case 'longHalfYear':
                    result = ORDINAL_NUMBERS[tmpValue] != null ? ORDINAL_NUMBERS[tmpValue] + ' Half' : result;
                    break;
                case 'shortHalfYear':
                    result = ORDINAL_NUMBERS[tmpValue] || result;
                    break;
            }

            break;

        //LL: 17.10.18: value = 1 or 2
        case TIME_PART_TYPES.HALF_FISCAL_YEAR:
            actualFormat = isDefaultFormat ? 'default' : format;

            switch (actualFormat) {
                case 'default':
                    break;
                case 'longHalfYear':
                    result = ORDINAL_NUMBERS[tmpValue] != null ? ORDINAL_NUMBERS[tmpValue] + ' Half' : result;
                    break;
                case 'shortHalfYear':
                    result = ORDINAL_NUMBERS[tmpValue] || result;
                case 'longHalfYearFiscal':
                    result = ORDINAL_NUMBERS[tmpValue] != null ? ' HalfF' + ORDINAL_NUMBERS[tmpValue] : result;
                    break;
                case 'shortHalfYearFiscal':
                    result = ORDINAL_NUMBERS[tmpValue] != null ? 'F' + ORDINAL_NUMBERS[tmpValue] : result;
                    break;
            }

            break;

        case TIME_PART_TYPES.QUARTER:
            if (isDateValue) {
                result = Globalization.formatDate(tmpValue, { raw: 'QQQQ' });
            } else {
                actualFormat = isDefaultFormat ? 'default' : format;

                switch (actualFormat) {
                    case 'default':
                    case 'Q':
                        break;

                    case 'latin':
                    case 'Qlatin':
                        result = LATIN_NUMBERS[tmpValue] || result;
                        break;
                }

                if (actualFormat === 'Qlatin' || actualFormat === 'Q') {
                    result = 'Q' + result;
                }
            }

            break;

        //LL: 17.10.18: dataType = "quarterFiscal", value = 1,2,3,4
        //    a = ['Q_N', 'latin_N', 'Qlatin_N', 'Q_Fiscal', 'latin_Fiscal', 'Qlatin_Fiscal'];
        case TIME_PART_TYPES.FISCAL_QUARTER:
            actualFormat = isDefaultFormat ? 'default' : format;

            switch (actualFormat) {
                case 'default':
                case 'Q':
                case 'Q_N':
                case 'Q_Fiscal':
                    break;

                case 'latin':
                case 'latin_N':
                case 'Qlatin':
                case 'Qlatin_N':
                case 'latin_Fiscal':
                case 'Qlatin_Fiscal':
                    result = LATIN_NUMBERS[tmpValue] || result;
                    break;
            }

            if (
                actualFormat === 'Qlatin' ||
                actualFormat === 'Q' ||
                actualFormat === 'Qlatin_Fiscal' ||
                actualFormat === 'Q_Fiscal'
            ) {
                result = 'Q' + result;
            }

            if (actualFormat === 'Q_Fiscal' || actualFormat === 'latin_Fiscal' || actualFormat === 'Qlatin_Fiscal') {
                result = 'F' + result;
            }

            break;

        case TIME_PART_TYPES.MONTH:
            if (isDateValue) {
                actualFormat = isDefaultFormat ? 'longName' : format;

                switch (actualFormat) {
                    case 'shortName':
                        result = Globalization.formatDate(tmpValue, { skeleton: 'MMM' });
                        break;

                    default:
                        result = Globalization.formatDate(tmpValue, { skeleton: 'MMMM' });
                        break;
                }
            } else {
                actualFormat = isDefaultFormat ? 'numbers' : format;

                switch (actualFormat) {
                    case 'longName':
                        result = Globalization.getMonthName_Wide(tmpValue);

                        if (result == null) {
                            //LL: 14.11.18
                            result = tmpValue.toString();
                        }
                        break;

                    case 'shortName':
                        result = Globalization.getMonthName_Abbreviated(tmpValue);
                        if (result == null) {
                            //LL: 14.11.18
                            result = tmpValue.toString();
                        }
                        break;

                    case 'numbers':
                        result = DateTimeFunctions.TwoDigits(tmpValue);
                        break;
                }
            }

            break;

        case TIME_PART_TYPES.DAY_OF_WEEK:
            actualFormat = isDefaultFormat ? 'longName' : format;

            let N = tmpValue;
            let themeShift = 0;

            if (firstDayOfWeek != null && Globalization.getFirstDay() !== firstDayOfWeek) {
                themeShift = Globalization.getFirstWeekDayNumber() - Globalization.weekDayNumber(firstDayOfWeek);
                N = N - themeShift;
            }

            if (N < 0) {
                N = 7 + N;
            }

            switch (actualFormat) {
                case 'longName':
                    result = Globalization.getWeekDayName_Wide(N - 1);

                    if (result == null) {
                        //LL: 14.11.18
                        result = tmpValue.toString();
                    }
                    break;

                case 'shortName':
                    result = Globalization.getWeekDayName_Abbreviated(N - 1);

                    if (result == null) {
                        //LL: 14.11.18
                        result = tmpValue.toString();
                    }
                    break;

                case 'minName':
                    result = Globalization.getWeekDayName_Narrow(N - 1);

                    if (result == null) {
                        //LL: 14.11.18
                        result = tmpValue.toString();
                    }
                    break;

                case 'numbers':
                    break;
            }

            break;

        default:
            if (tmpValue === '') {
                return '';
            }

            actualFormat = isDefaultFormat ? timePart : format;

            switch (actualFormat) {
                case 'quarter':
                    result = Globalization.formatDate(tmpValue, { raw: 'QQQQ' });
                    break;

                case 'quarterAndYear':
                    result = Globalization.formatDate(tmpValue, { skeleton: 'yQQQ' }); //like "4-й кв. 2016 г."
                    break;

                case 'dateLongTime':
                case 'dateShortTime':
                case 'longTime':
                case 'shortTime':
                case 'longDateLongTime':
                    pattern = Globalization.getFormatSpecifier(actualFormat, use24hours);
                    result = Globalization.formatDate(tmpValue, pattern);
                    break;

                case 'longDate':
                    result = Globalization.formatDate(tmpValue, { date: 'long' });
                    break;

                case 'shortDate':
                    result = Globalization.formatDate(tmpValue, { date: 'short' });
                    break;

                case 'monthAndDay':
                    result = Globalization.formatDate(tmpValue, { skeleton: 'MMMMd' });
                    break;

                case 'monthAndYear':
                    result = Globalization.formatDate(tmpValue, { skeleton: 'yMMMM' });
                    break;

                case 'year':
                    result = Globalization.formatDate(tmpValue, { skeleton: 'y' });
                    break;

                case 'halfyear':
                    const quarter = Globalization.formatDate(tmpValue, { raw: 'Q' }); // quarter as string  "3"

                    switch (quarter) {
                        case '1':
                        case '2':
                            result = '1';
                            break;
                        case '3':
                        case '4':
                            result = '2';
                            break;
                        default:
                            result = '0';
                            break;
                    }
                    break;

                case 'month':
                    result = Globalization.formatDate(tmpValue, { raw: 'LLLL' });
                    break;

                case 'day':
                    result = Globalization.formatDate(tmpValue, { skeleton: 'd' });
                    break;

                case 'hour':
                case 'HH':
                    result = Globalization.formatDate(tmpValue, { raw: 'HH' });
                    break;

                case 'minute':
                case 'mm':
                    result = Globalization.formatDate(tmpValue, { raw: 'mm' });
                    break;

                case 'second':
                case 'ss':
                    result = Globalization.formatDate(tmpValue, { raw: 'ss' });
                    break;

                case 'millisecond':
                    result = Globalization.formatDate(tmpValue, { raw: 'SSSS' });
                    break;

                case 'custom':
                    if (customFormat == null) {
                        result = Globalization.formatDate(tmpValue, { datetime: 'full' });
                    } else {
                        let res: any;

                        try {
                            res = Globalization.formatDate(tmpValue, {
                                raw: customFormat
                            });
                        } catch (e) {
                            const errorText = `Fail to convert '${tmpValue}' of Date type to format: ${customFormat}.`;

                            console.log(`${errorText} - AttrQueryValuesFormat.convertDate(). Error: ${e.message}.`);

                            if (onConverionErrorCallback != null) {
                                onConverionErrorCallback(errorText);
                            }

                            res = Globalization.formatDate(tmpValue, { datetime: 'full' });
                        }

                        if (res != null) {
                            result = res;
                        }
                    }
                    break;

                default:
                    result = Globalization.formatDate(tmpValue, { datetime: 'full' });
                    break;
            }
    }

    return result;
}

export function convertToDevExpressDataType(attributeType: string): DevExpressDataType {
    switch (attributeType) {
        case ATTRIBUTE_TYPES.STRING:
            return 'string';

        case ATTRIBUTE_TYPES.INTEGER:
        case ATTRIBUTE_TYPES.REAL:
            return 'number';

        case ATTRIBUTE_TYPES.ENUMERATION:
            return 'string';

        case ATTRIBUTE_TYPES.BOOLEAN:
            return 'boolean';

        case ATTRIBUTE_TYPES.DATE:
            return 'date';

        case ATTRIBUTE_TYPES.DATETIME:
        case ATTRIBUTE_TYPES.TIME:
            return 'datetime';
        //case ATTRIBUTE_TYPES.TIME: // VR: type TIME was missed
        //    return 'time';          // OE: type time is not supported by DevExpress

        default:
            return 'string';
    }
}

export function getAttributeTypeByAggregate(
    value: {
        agregate?: string;
        aggregateGroup?: string;
        expression?: IExpression;
    },
    attributeType?: string
) {
    if (isCountAggregate(value)) {
        return ATTRIBUTE_TYPES.INTEGER;
    } else if (isAvgAggregate(value)) {
        return ATTRIBUTE_TYPES.REAL;
    } else {
        if (value.expression != null) {
            return getExpressionType(value.expression);
        } else {
            return attributeType;
        }
    }
}

export function isCountAggregate(value: { agregate?: string; aggregateGroup?: string }) {
    let aggregate = value.agregate;

    if (aggregate == null) {
        aggregate = value.aggregateGroup;
    }

    return (
        aggregate != null && (aggregate === 'count' || aggregate === 'count distinct' || aggregate === 'count not null')
    );
}

export function isMinMaxAggregate(value: { aggregateGroup?: string }) {
    const aggregate = value.aggregateGroup;

    return aggregate != null && (aggregate === 'min' || aggregate === 'max');
}

export function isAvgAggregate(value: { aggregateGroup?: string }) {
    const aggregate = value.aggregateGroup;

    return aggregate != null && (aggregate === 'avg' || aggregate === 'avg distinct');
}

function year2digits(value) {
    let digit1 = value.toString().charAt(value.toString().length - 2);
    let digit2 = value.toString().charAt(value.toString().length - 1);

    return (digit1 ? digit1 : '0') + (digit2 ? digit2 : '0');
}

export function convertIntToDate(
    value,
    dataType: string,
    formats: IValueFormat,
    timePart?: string,
    onConverionErrorCallback?: IOnConverionErrorCallback
) {
    //let D = new Date();
    switch (formats.formatFront) {
        case 'longYear':
        case TIME_PART_FORMATS.FOUR_DIGITS:
            return value.toString();
        //convertDate(D.setFullYear(value, 0, 1), 'year', '4digits', dataType); //IL: WorkItem [105224]: Wrong format of timepart(year) to "4 digits" = BUG

        case 'shortYear':
        case TIME_PART_FORMATS.TWO_DIGITS:
            return year2digits(value);
        //convertDate(D.setFullYear(value, 0, 1), 'year', TIME_PART_FORMATS.TWO_DIGITS, dataType);//IL: WorkItem [105224]: Wrong format of timepart(year) to "4 digits" = BUG

        //LL: 17.10.18: dataType = "yearFiscal",  also used 'longYear' and 'shortYear' above
        case TIME_PART_FORMATS.LONG_FISCAL_YEAR:
            return 'FY' + value.toString();

        case TIME_PART_FORMATS.SHORT_FISCAL_YEAR:
            return 'FY' + year2digits(value);
        //convertDate(D.setFullYear(value, 0, 1), 'yearFiscal', formatFront.format, dataType);//IL: WorkItem [105224]: Wrong format of timepart(year) to "4 digits" = BUG

        case TIME_PART_FORMATS.LONG_FISCAL_YEAR_START_END:
            return (
                (formats.useFiscalPrefix === false ? '' : 'FY') +
                value +
                (formats.fiscalFirstMonth === 1 ? '' : (formats.fiscalYearDivider || '-') + (value + 1))
            );

        case TIME_PART_FORMATS.SHORT_FISCAL_YEAR_START_END:
            return (
                (formats.useFiscalPrefix === false ? '' : 'FY') +
                year2digits(value) +
                (formats.fiscalFirstMonth === 1 ? '' : (formats.fiscalYearDivider || '-') + year2digits(value + 1))
            );

        //LL: 17.10.18: And dataType = "halfYearFiscal"
        //LL: 02.10.18: dataType = "halfyear", value = 1 or 2
        case 'longHalfYear':
        case 'shortHalfYear':
            return convertDate(value, 'halfyear', formats.formatFront, dataType, onConverionErrorCallback);

        //LL: 17.10.18: dataType = "halfYearFiscal", value = 1 or 2
        case 'longHalfYearFiscal':
        case 'shortHalfYearFiscal':
            return convertDate(
                value,
                'halfYearFiscal',
                formats.formatFront,
                dataType,
                onConverionErrorCallback,
                null,
                false,
                formats.firstDayOfWeek,
                formats.fiscalFirstMonth,
                formats.useFiscalPrefix,
                formats.fiscalYearDivider
            );

        case 'Q':
            return convertDate(value, 'quarter', 'Q', dataType, onConverionErrorCallback);
        case 'latin':
            return convertDate(value, 'quarter', 'latin', dataType, onConverionErrorCallback);
        case 'Qlatin':
            return convertDate(value, 'quarter', 'Qlatin', dataType, onConverionErrorCallback);

        //LL: 17.10.18: dataType = "quarterFiscal", value = 1,2,3,4
        //    a = ['Q_N', 'latin_N', 'Qlatin_N', 'Q_Fiscal', 'latin_Fiscal', 'Qlatin_Fiscal'];
        case 'Q_N':
            return convertDate(value, 'quarter', 'Q', dataType, onConverionErrorCallback);
        case 'latin_N':
            return convertDate(value, 'quarter', 'latin', dataType, onConverionErrorCallback);
        case 'Qlatin_N':
            return convertDate(value, 'quarter', 'Qlatin', dataType, onConverionErrorCallback);
        case 'Q_Fiscal':
        case 'latin_Fiscal':
        case 'Qlatin_Fiscal':
            return convertDate(value, 'quarterFiscal', formats.formatFront, dataType, onConverionErrorCallback);

        case 'longMonth':
            return convertDate(value, 'month', 'longName', dataType, onConverionErrorCallback);

        case 'shortMonth':
            return convertDate(value, 'month', 'shortName', dataType, onConverionErrorCallback);

        case 'longweekDay':
            return convertDate(
                value,
                'weekday',
                'longName',
                dataType,
                onConverionErrorCallback,
                null,
                null,
                formats.firstDayOfWeek
            );

        case 'shortweekDay':
            return convertDate(
                value,
                'weekday',
                'shortName',
                dataType,
                onConverionErrorCallback,
                null,
                null,
                formats.firstDayOfWeek
            );

        case 'minweekDay':
        case 'minWeekDay':
            return convertDate(
                value,
                'weekday',
                'minName',
                dataType,
                onConverionErrorCallback,
                null,
                null,
                formats.firstDayOfWeek
            );

        case 'shortName':
            return convertDate(
                value,
                timePart,
                'shortName',
                dataType,
                onConverionErrorCallback,
                null,
                null,
                formats.firstDayOfWeek
            );
        case 'longName':
            return convertDate(
                value,
                timePart,
                'longName',
                dataType,
                onConverionErrorCallback,
                null,
                null,
                formats.firstDayOfWeek
            );
        case 'minName':
            return convertDate(
                value,
                timePart,
                'minName',
                dataType,
                onConverionErrorCallback,
                null,
                null,
                formats.firstDayOfWeek
            );

        case 'Days':
            return (value / 60.0 / 60.0 / 24.0).toFixed(2);

        case 'Hours':
            return (value / 60.0 / 60.0).toFixed(2);

        case 'Minutes':
            return (value / 60.0).toFixed(2);

        //LL:  these are Time Intervals:  Numeric value interpret as Time Interval:

        //LL 22.12.19: value is in hours
        case 'Hours DD:HH':
            //LL: do not format as Time, it is wrong: TZ and AM/PM
            return toTimeInterval_hours_dh(value);

        //LL 22.12.19: value is in minutes
        case 'Minutes DD:HH:MM':
            //LL: do not format as Time, it is wrong: TZ and AM/PM
            return toTimeInterval_minutes_dhm(value);

        //LL 28.12.17: value is in minutes
        case 'Minutes HH:MM':
            //LL: do not format as Time, it is wrong: TZ and AM/PM
            return toTimeInterval_minutes_hm(value);

        //LL 28.12.17: value is in seconds
        case 'Seconds HH:MM:SS':
            //LL: do not format as Time, it is wrong: TZ and AM/PM
            return toTimeInterval_seconds_hms(value);

        //LL 28.12.17: value is in seconds
        case 'Seconds MM:SS':
            //LL: do not format as Time, it is wrong: TZ and AM/PM
            return toTimeInterval_seconds_ms(value);

        //LL 22.12.19: add TimeIntervals formats from "r":

        //LL 28.12.17: value is in hours      from "r"
        case 'Hours HH:MM':
            return toTimeInterval(value);

        //LL 28.12.17: value is in hours     from "r"
        case 'Hours HH:MM:SS':
            return toTimeInterval_hours_hms(value);

        //LL 28.12.17: value is in minutes    from "r"
        case 'Minutes MM:SS':
            return toTimeInterval(value);

        //LL 28.12.17: value is in minutes    from "r"
        case 'Minutes HH:MM:SS':
            return toTimeInterval_minutes_hms(value);

        case 'Seconds':
        default:
            if (timePart != null) {
                formats.useDigitGrouping = false;
                formats.formatZeroAsBlank = false;
                formats.precision = 0;
            }

            return formatNumeric(value, formats, undefined, onConverionErrorCallback);
    }
}

//LL 28.12.17
export function convertRealToDate(
    value: any,
    formatFront: IValueFormat,
    onConverionErrorCallback?: IOnConverionErrorCallback
) {
    //  https://github.com/globalizejs/globalize/blob/master/doc/api/date/date-formatter.md
    //     { skeleton: "Ehms" } "Tue 5:55:00 PM"
    //     { skeleton: "Hms" }  "17:55:00"
    //     { skeleton: "Hm" }   "17:55"
    //     { skeleton: "ms" }   "55:00"

    //LL: do not format as Time, it is wrong: TZ and AM/PM !!

    switch (formatFront.formatFront) {
        //LL:  these are Time Intervals:  Numeric value interpret as Time Interval:

        //LL 28.12.17: value is in hours
        case 'Hours HH:MM':
            return toTimeInterval(value);

        //LL 28.12.17: value is in hours
        case 'Hours HH:MM:SS':
            return toTimeInterval_hours_hms(value);

        //LL 28.12.17: value is in minutes
        case 'Minutes MM:SS':
            return toTimeInterval(value);

        //LL 28.12.17: value is in minutes
        case 'Minutes HH:MM:SS':
            return toTimeInterval_minutes_hms(value);

        //LL 22.12.19: add TimeIntervals formats from "i":

        //LL 22.12.19: value is in hours
        case 'Hours DD:HH':
            return toTimeInterval_hours_dh(value);

        //LL 22.12.19: value is in minutes
        case 'Minutes DD:HH:MM':
            return toTimeInterval_minutes_dhm(value);

        //LL 28.12.17: value is in minutes
        case 'Minutes HH:MM':
            return toTimeInterval_minutes_hm(value);

        //LL 28.12.17: value is in seconds
        case 'Seconds HH:MM:SS':
            return toTimeInterval_seconds_hms(value);

        //LL 28.12.17: value is in seconds
        case 'Seconds MM:SS':
            return toTimeInterval_seconds_ms(value);

        default:
            return formatNumeric(value, formatFront, undefined, onConverionErrorCallback);
    }
}

export function formatNumeric(
    valueToFormat,
    formatFront: IValueFormat,
    nullHandler?: NullHandling,
    onConverionErrorCallback?: IOnConverionErrorCallback
) {
    if (valueToFormat == null) {
        if (nullHandler && nullHandler.getBehaviour() === nullHandlingBehaviour.REPLACE_NULLS) {
            //return nullHandler.handleNulls(valueToFormat, 'number');
            //LL: 04.06.18:  we must return formatted value, but not just replacing value !
            valueToFormat = nullHandler.handleNulls(valueToFormat, 'number');

            if (typeof valueToFormat === 'string') {
                return valueToFormat;
            }
        }
    }

    let formatObject = parseNumberFormatString(formatFront.formatFront);

    if (formatObject.power === LargeNumberFormatPowers.largenumber) {
        formatObject.power = calculateNumberPower(valueToFormat, 1000, 0, MAX_LARGE_NUMBER_POWER);
    }

    if (formatObject.power) {
        valueToFormat = getNumberByPower(valueToFormat, formatObject.power, 1000);
    }

    if (formatObject.formatType === 'currency') {
        return formatCurrencyCore(
            valueToFormat,
            formatObject.power,
            formatFront.precision,
            formatFront.currency,
            formatFront.useDigitGrouping,
            formatFront.formatZeroAsBlank
        );
    } else {
        const actualUseDigitGrouping = getActualUseDigitGrouping(formatFront.useDigitGrouping);
        const largeNumberPattern = getCLDRPatternForLargeNumber(formatObject.power);
        //let customFormat = formatFront.customFormat;  //VR: was missed: = formatFront.customFormat;

        if (largeNumberPattern != null) {
            //formatObject.formatType = 'custom';
            //customFormat = Globalization.getCLDRValue(largeNumberPattern);

            //LL: 18.04.19:  see my comments about format patterns in formatCurrencyCore
            let pattern = Globalization.getCLDRValue(largeNumberPattern);
            let patternStandard = '#,##0.00';

            if (pattern == null) {
                //pattern = '0M'; // = USA
                //LL: 21.02.25:
                pattern = '0' + getDefaultMarkForLargeNumber(formatObject.power);
            }
            pattern = pattern.substr(1);
            pattern = patternStandard + pattern;

            // VR has added it:
            return Globalization.formatNumber(
                //VR: use precision here if you have set CUSTOM format by yourself!!
                valueToFormat,
                {
                    //raw: Globalization.getCLDRValue(largeNumberPattern),
                    raw: pattern, //LL: 18.04.19
                    minimumFractionDigits: formatFront.precision || 0,
                    maximumFractionDigits: formatFront.precision || 0,
                    useGrouping: actualUseDigitGrouping
                }
            );
        }

        return formatNumberCore(
            valueToFormat,
            formatObject.formatType,
            formatFront.precision,
            formatFront.customFormat,
            formatFront.useDigitGrouping,
            formatFront.formatZeroAsBlank,
            onConverionErrorCallback
        ); // + powerPostfix;
    }
}

const MAX_LARGE_NUMBER_POWER = 4;
const DECIMAL_BASE = 10;

const NumericFormat = {
    currency: 'C',
    fixedpoint: 'N',
    exponential: '',
    percent: 'P',
    decimal: 'D',
    timeinterval: 'T' //LL: 12.04.17
};

const LargeNumberFormatPowers = {
    largenumber: 9999999999,
    thousands: 1,
    millions: 2,
    billions: 3,
    trillions: 4
};

//LL: 21.02.25: use when Globalization.getCLDRValue(largeNumberPattern) returns null (for Chinise)
function getDefaultMarkForLargeNumber(power: number): string {
    //LL: en-USA
    switch (power) {
        case 1:
            return 'K';
        case 2:
            return 'M';
        case 3:
            return 'B';
        case 4:
            return 'T';
        default:
            return ' ';
    }
}

function getCLDRPatternForLargeNumber(power: number): string {
    switch (power) {
        case 1:
            return 'numbers/decimalFormats-numberSystem-latn/short/decimalFormat/1000-count-one';
        case 2:
            return 'numbers/decimalFormats-numberSystem-latn/short/decimalFormat/1000000-count-one';
        case 3:
            return 'numbers/decimalFormats-numberSystem-latn/short/decimalFormat/1000000000-count-one';
        case 4:
            return 'numbers/decimalFormats-numberSystem-latn/short/decimalFormat/1000000000000-count-one';
        default:
            return null;
    }
}

function getCLDRPatternForLargeCurrency(power: number): string {
    switch (power) {
        case 1:
            return 'numbers/currencyFormats-numberSystem-latn/short/standard/1000-count-one';
        case 2:
            return 'numbers/currencyFormats-numberSystem-latn/short/standard/1000000-count-one';
        case 3:
            return 'numbers/currencyFormats-numberSystem-latn/short/standard/1000000000-count-one';
        case 4:
            return 'numbers/currencyFormats-numberSystem-latn/short/standard/1000000000000-count-one';
        default:
            return null;
    }
}

function parseNumberFormatString(format: string) {
    let formatObject = {
        formatType: 'fixedpoint',
        power: 0
    };

    if (!format || typeof format !== 'string') {
        return formatObject;
    }

    if (format === 'largeNumber') {
        return {
            formatType: 'decimal',
            power: 9999999999
        };
    }

    if (format === 'custom') {
        return {
            formatType: 'custom',
            power: 0
        };
    }

    const formatList = format.toLowerCase().split(' ');

    _.each(formatList, (value) => {
        if (value in NumericFormat) {
            formatObject.formatType = value;
        } else if (value in LargeNumberFormatPowers) {
            formatObject.power = LargeNumberFormatPowers[value];
        }
    });

    if (formatObject.power && !formatObject.formatType) {
        formatObject.formatType = 'fixedpoint';
    }

    if (formatObject.formatType) {
        return formatObject;
    }
}

function calculateNumberPower(value, base, minPower?, maxPower?) {
    let number = Math.abs(value);
    let power = 0;

    if (number > 1) {
        while (number && number >= base && (maxPower === undefined || power < maxPower)) {
            power++;
            number = number / base;
        }
    } else if (number > 0 && number < 1) {
        while (number < 1 && (minPower === undefined || power > minPower)) {
            power--;
            number = number * base;
        }
    }
    return power;
}

function getNumberByPower(number, power, base) {
    let result = number;

    while (power > 0) {
        result = result / base;
        power--;
    }

    while (power < 0) {
        result = result * base;
        power++;
    }

    return result;
}

function formatNumberCore(
    value: any,
    format: string,
    precision: string | number,
    customFormat: string,
    useDigitGrouping: boolean,
    formatZeroAsBlank: boolean,
    onConverionErrorCallback: IOnConverionErrorCallback
) {
    if (value === 0 && formatZeroAsBlank === true) {
        return '';
    }

    const actualPrecision = (typeof precision === 'string' ? parseInt(precision) : precision) || 0;
    const actualUseDigitGrouping = getActualUseDigitGrouping(useDigitGrouping);

    //EVG: Sometimes Globalization.formatNumber() complains that a valid string with number is not a number.
    if (typeof value !== 'number') {
        value = Number(value);
    }

    switch (format) {
        case 'exponential':
            return formatNumberExponential(value, actualPrecision, actualUseDigitGrouping, onConverionErrorCallback);

        case 'fixedpoint':
            return Globalization.formatNumber(value, {
                style: 'decimal',
                minimumFractionDigits: actualPrecision,
                maximumFractionDigits: actualPrecision,
                useGrouping: actualUseDigitGrouping
            });

        case 'percent':
            return Globalization.formatNumber(value, {
                style: 'percent',
                minimumFractionDigits: actualPrecision,
                maximumFractionDigits: actualPrecision
            });

        case 'timeinterval':
            return toTimeInterval(value);

        case 'custom':
            try {
                return Globalization.formatNumber(value, {
                    raw: customFormat
                });
            } catch (e) {
                const errorText = `Fail to convert '${value}' of Number type to format: ${customFormat}.`;

                console.log(`${errorText} - AttrQueryValuesFormat.formatNumberCore(). Error: ${e.message}.`);

                if (onConverionErrorCallback != null) {
                    onConverionErrorCallback(errorText);
                }

                return value;
            }

        case 'decimal':
        default:
            return Globalization.formatNumber(value, {
                style: 'decimal',
                minimumFractionDigits: 0,
                maximumFractionDigits: actualPrecision,
                useGrouping: actualUseDigitGrouping
            });
    }
}

//LL: "hours" to format "hh:mm",  or "minutes" to format "mm:ss"
//  also need "hours" to format "hh:mm:ss", "minutes" to format "hh:mm:ss"     Type="r"
export function toTimeInterval(val: number): string {
    //  7.2 => 07:12
    let x = val;

    const isNeg = x < 0;

    if (isNeg) {
        x = -x;
    }

    let y = Math.floor(x);
    let z = x - y;

    z = z * 60;

    //z = Math.floor(z);
    z = Math.round(z); // 59,77 -> 60

    if (z >= 60) {
        // === 60
        z = 0;
        y++;
    }

    const sign = isNeg ? '-' : '';
    //return sign + Utils.TwoDigits(y) + ':' + Utils.TwoDigits(z);
    return sign + y.toString() + ':' + DateTimeFunctions.TwoDigits(z); //LL: 24.07.20: Leo wants 1st value without leading zero
}

//LL 27.12.17: "hours" to format "hh:mm:ss"  Type="r"
export function toTimeInterval_hours_hms(val: number): string {
    //  7.22 => 07:13:12
    let x = val;

    const isNeg = x < 0;

    if (isNeg) {
        x = -x;
    }

    const y = Math.floor(x);
    let z = x - y;

    z = z * 60; // minutes   0.22 -> 13,2

    let m = Math.floor(z);
    let s = z - m;

    s = s * 60; // seconds   0.2 -> 12

    //s = Math.floor(s);
    s = Math.round(s); // 59,77 -> 60

    if (s >= 60) {
        // === 60
        s = 0;
        m++;
    }

    const sign = isNeg ? '-' : '';
    //return sign + Utils.TwoDigits(y) + ':' + Utils.TwoDigits(m) + ':' + Utils.TwoDigits(s);
    return sign + y.toString() + ':' + DateTimeFunctions.TwoDigits(m) + ':' + DateTimeFunctions.TwoDigits(s); //LL: 24.07.20: Leo wants 1st value without leading zero
}

//LL 28.12.17: "minutes" to format "hh:mm:ss"  Type="r"
export function toTimeInterval_minutes_hms(val: number): string {
    //  7.22 => 07:13:12
    let x = val;

    const isNeg = x < 0;

    if (isNeg) {
        x = -x;
    }

    x = x / 60; // to hours

    const y = Math.floor(x);
    let z = x - y;

    z = z * 60; // minutes   0.22 -> 13,2

    let m = Math.floor(z);
    let s = z - m;

    s = s * 60; // seconds   0.2 -> 12

    //s = Math.floor(s);
    s = Math.round(s); // 59,77 -> 60

    if (s >= 60) {
        // === 60
        s = 0;
        m++;
    }

    const sign = isNeg ? '-' : '';
    //return sign + Utils.TwoDigits(y) + ':' + Utils.TwoDigits(m) + ':' + Utils.TwoDigits(s);
    return sign + y.toString() + ':' + DateTimeFunctions.TwoDigits(m) + ':' + DateTimeFunctions.TwoDigits(s); //LL: 24.07.20: Leo wants 1st value without leading zero
}

//LL 28.12.17: "seconds" to format "hh:mm:ss"   Type="i"
export function toTimeInterval_seconds_hms(val: number): string {
    let x = val;
    const isNeg = x < 0;

    if (isNeg) {
        x = -x;
    }

    x = x / (60 * 60); // to hours

    const y = Math.floor(x); // hours
    let z = x - y;
    z = z * 60;

    let m = Math.floor(z); // minutes
    let s = z - m;
    s = s * 60; // seconds

    //s = Math.floor(s);
    s = Math.round(s); // 59,77 -> 60
    if (s >= 60) {
        // === 60
        s = 0;
        m++;
    }

    const sign = isNeg ? '-' : '';
    //return sign + Utils.TwoDigits(y) + ':' + Utils.TwoDigits(m) + ':' + Utils.TwoDigits(s);
    return sign + y.toString() + ':' + DateTimeFunctions.TwoDigits(m) + ':' + DateTimeFunctions.TwoDigits(s); //LL: 24.07.20: Leo wants 1st value without leading zero
}

//LL 28.12.17: "seconds" to format "mm:ss"   Type="i"
export function toTimeInterval_seconds_ms(val: number): string {
    let x = val;
    const isNeg = x < 0;
    if (isNeg) {
        x = -x;
    }

    x = x / 60; // to minutes

    let y = Math.floor(x); // minutes
    let z = x - y;
    z = z * 60; // seconds

    //z = Math.floor(z);
    z = Math.round(z); // 59,77 -> 60
    if (z >= 60) {
        // === 60
        z = 0;
        y++;
    }

    const sign = isNeg ? '-' : '';

    //return sign + Utils.TwoDigits(y) + ':' + Utils.TwoDigits(z);
    return sign + y.toString() + ':' + DateTimeFunctions.TwoDigits(z); //LL: 24.07.20: Leo wants 1st value without leading zero
}

//LL 28.12.17: "minutes" to format "hh:mm"   Type="i"
export function toTimeInterval_minutes_hm(val: number): string {
    return toTimeInterval_seconds_ms(val);
}

//LL 22.12.19: "minutes" to format "dd:hh:mm"   Type="i"
export function toTimeInterval_minutes_dhm(val: number): string {
    let x = val;
    const isNeg = x < 0;
    if (isNeg) {
        x = -x;
    }

    x = x / (60 * 24); // to days

    const y = Math.floor(x); // days
    let z = x - y;
    z = z * 24;

    let m = Math.floor(z); // hours
    let s = z - m;
    s = s * 60; // minutes

    //s = Math.floor(s);
    s = Math.round(s); // 59,77 -> 60
    if (s >= 60) {
        // === 60
        s = 0;
        m++;
    }

    const sign = isNeg ? '-' : '';
    //return sign + Utils.TwoDigits(y) + ':' + Utils.TwoDigits(m) + ':' + Utils.TwoDigits(s);
    return sign + y.toString() + ':' + DateTimeFunctions.TwoDigits(m) + ':' + DateTimeFunctions.TwoDigits(s); //LL: 24.07.20: Leo wants 1st value without leading zero
}

//LL 22.12.19: "hours" to format "dd:hh"   Type="i"
export function toTimeInterval_hours_dh(val: number): string {
    let x = val;

    const isNeg = x < 0;

    if (isNeg) {
        x = -x;
    }

    x = x / 24; // to days

    let y = Math.floor(x); // days
    let z = x - y;
    z = z * 24; // hours

    //z = Math.floor(z);
    z = Math.round(z); // 59,77 -> 60
    if (z >= 24) {
        // === 24
        z = 0;
        y++;
    }

    const sign = isNeg ? '-' : '';
    //return sign + Utils.TwoDigits(y) + ':' + Utils.TwoDigits(z);
    return sign + y.toString() + ':' + DateTimeFunctions.TwoDigits(z); //LL: 24.07.20: Leo wants 1st value without leading zero
}

function formatCurrencyCore(
    value,
    power,
    precision: number,
    currency,
    useDigitGrouping: boolean,
    formatZeroAsBlank: boolean
) {
    if (value === 0 && formatZeroAsBlank === true) {
        return '';
    }

    const actualUseDigitGrouping = getActualUseDigitGrouping(useDigitGrouping);
    const actualPrecision = precision || 0;

    let currencyValid = 'USD';

    if (currency != undefined && currency.length == 3) {
        currencyValid = currency;
    }

    let largeNumberPattern = getCLDRPatternForLargeCurrency(power);

    if (largeNumberPattern != null) {
        let currencySymbol = Globalization.getCLDRValue('numbers/currencies/' + currencyValid).symbol;

        //let pattern = Globalization.getCLDRValue(largeNumberPattern);

        //LL:  about Format Patterns ==============================================
        //LL: 18.04.19: try for bug: group-delimiter is not shown.
        //                 and I see that decimalDigets also are not applied
        //LL: see https://github.com/unicode-cldr/cldr-numbers-modern/blob/master/main/ru/numbers.json
        //    'numbers/currencyFormats-numberSystem-latn/short/standard/1000-count-one':   "0 тыс'.' ¤"
        //    'numbers/currencyFormats-numberSystem-latn/short/standard/1000000-count-one':  "0 млн ¤"
        //    'numbers/currencyFormats-numberSystem-latn/short/standard/1000000000-count-one':  "0 млрд ¤"
        //    'numbers/currencyFormats-numberSystem-latn/short/standard/1000000000000-count-one':  "0 трлн ¤"
        //  "1000-count-one": "0 тыс'.' ¤"   //LL: i.e. without decimals and without group-delimiter

        //    currencyFormats-numberSystem-latn/"standard": "#,##0.00 ¤"    // with decimals and group-delimiter

        //LL: see http://cldr.unicode.org/translation/number-patterns
        //   Pattern Characters
        //Numbers are formatted using patterns, like "#,###.00".
        // For example, the pattern "#,###.00" when used to format the number 12345.678 could result in "12'345,67".
        // That would happen if the grouping separator for your language is an apostrophe ('), and the decimal separator is a comma (,).
        // Also see Number Symbols.
        //
        //    Important:
        // The characters . , 0 # (and others below) are special placeholders;
        // they stand for the decimal separator, and so on, and are NOT real characters.
        // You must NOT "translate" the placeholders; for example, don't change '.' to ','
        // even though in your language the decimal point is written with a comma.
        //
        //    Here are the special characters used in number patterns.
        // Whenever any of these symbols are in the English pattern,
        // they must be retained in the pattern for your language.
        // The positions of some of them (%, ¤) may be changed, or spaces added or removed.
        // The symbols will be replaced by the local equivalents, using the Number Symbols for your language.

        //  .   Replaced automatically by the character used for the decimal point in your language. Not a real period; must be retained!
        //  ,   Replaced by the "grouping" (thousands) separator in your language. Not a real comma; must be retained!
        //  0   Replaced by a digit (or zero if there aren't enough digits).
        //  #   Replaced by a digit (or nothing if there aren't enough). Often used to show the position of the ",".
        //  ¤   This will be replaced by a currency symbol, such as $ or USD.
        //      Note: by default a space is automatically added between letters in a currency symbol and adjacent numbers.
        //      So you don't need to add a space between them if your language writes "$12" but "USD 12".
        // '    If any of the above characters are used as literal characters, they must be quoted with ASCII single quotes.
        //      For example, in the Short Numbers if a period needs to be used to mark an abbreviation, it would appear as:
        //          0.0 tis'.' not  0.0 tis.
        //etc

        //LL: note:
        // It seems that if format pattern does not contain a group-delimiter (symbol ','), "useGrouping" option is not applied.
        // Only if a group-delimiter exist, "useGrouping" option either shows group-delimiter or not
        //-------------------------------------------------------------------
        //LL: I suppose that it is some (maybe new) BUG OF Globalize and cldr
        //-------------------------------------------------------------------

        //LL: =====================================================================

        //LL: 18.04.19:
        //   "standard": "#,##0.00 ¤"
        //let pattern = "#,##0.00 ¤";  // ok
        //let pattern = "#,##0.00 LLL'.' ¤";   // ok
        //LL: try to insert "standard" pattern into 1000-count-one' ("0 тыс'.' ¤"), etc
        //       change leading '0' to "#,##0.00"
        //
        //LL: 22.04.19: currency symbol can be the 1st: "¤0K"
        let pattern = Globalization.getCLDRValue(largeNumberPattern);
        let patternStandard = '#,##0.00';
        //pattern = pattern.substr(1);
        //pattern = patternStandard + pattern;
        //LL: 22.04.19: "¤0K" or "0 тыс'.' ¤"
        if (pattern && pattern.length > 0) {
            if (pattern.substr(0, 1) === '¤') {
                // "¤0K"
                let k = pattern.indexOf('0');

                if (k > 0) {
                    pattern = pattern.substr(0, k) + patternStandard + pattern.substr(k + 1);
                }
            } else {
                // "0 тыс'.' ¤"
                pattern = pattern.substr(1);
                pattern = patternStandard + pattern;
            }
        }

        return Globalization.formatNumber(value, {
            raw: pattern,
            minimumFractionDigits: actualPrecision,
            maximumFractionDigits: actualPrecision,
            useGrouping: actualUseDigitGrouping
        }).replace('¤', currencySymbol);
    } else {
        let result;

        try {
            result = Globalization.formatCurrency(value, currencyValid, {
                style: 'symbol',
                minimumFractionDigits: actualPrecision,
                maximumFractionDigits: actualPrecision,
                useGrouping: actualUseDigitGrouping
            });
        } catch {
            result = Globalization.formatCurrency(value, currencyValid, {
                style: 'code',
                minimumFractionDigits: actualPrecision,
                maximumFractionDigits: actualPrecision,
                useGrouping: actualUseDigitGrouping
            });
        }

        return result;
    }
}

function formatNumberExponential(
    value: number,
    precision: number,
    useGrouping: boolean,
    onConverionErrorCallback: IOnConverionErrorCallback
) {
    let power = calculateNumberPower(value, DECIMAL_BASE);
    let number = getNumberByPower(value, power, DECIMAL_BASE);

    if (precision == null) {
        precision = 1;
    }

    if (number.toFixed(precision || 0) >= DECIMAL_BASE) {
        power++;
        number = number / DECIMAL_BASE;
    }

    const powString = (power >= 0 ? '+' : '') + power.toString();

    return (
        formatNumberCore(number, 'fixedpoint', precision, 'USD', useGrouping, false, onConverionErrorCallback) +
        'E' +
        powString
    );
}

export function formatValue(
    value: any,
    valueText: any,
    dataType?: string,
    format?: IValueFormat,
    timePart?: string,
    nullHandler?: NullHandling,
    onConverionErrorCallback?: IOnConverionErrorCallback
): string {
    if (dataType === ATTRIBUTE_TYPES.ANYTYPE && format != null && format.formatAsType != null) {
        dataType = format.formatAsType;
    }

    if (value == null) {
        if (nullHandler && nullHandler.getBehaviour() === nullHandlingBehaviour.REPLACE_NULLS) {
            //return nullHandler.handleNulls(value, dataType);
            //LL: 04.06.18:  we must return formatted value, but not just replacing value !
            value = nullHandler.handleNulls(value, dataType);

            if (typeof value === 'string') {
                return value;
            }
        } else {
            return valueText;
        }
    }

    if (format == null) {
        switch (dataType) {
            case ATTRIBUTE_TYPES.DATE:
                return convertDate(value, timePart, null, dataType, onConverionErrorCallback);

            default:
                return valueText;
        }
    }

    switch (dataType) {
        case 'number':
        case ATTRIBUTE_TYPES.INTEGER:
            if (typeof value === 'string') {
                const num = Number(value);

                if (!isNaN(num)) {
                    value = num;
                }
            }

            return convertIntToDate(value, dataType, format, timePart, onConverionErrorCallback);

        case ATTRIBUTE_TYPES.REAL:
            return convertRealToDate(value, format, onConverionErrorCallback);

        case 'boolean':
        case ATTRIBUTE_TYPES.BOOLEAN:
            switch (format.formatFront) {
                case 'YesEmpty':
                    return value ? 'Yes' : '';

                case 'YesNo':
                    return value ? 'Yes' : 'No';

                case 'TrueFalse':
                    return value ? 'True' : 'False';

                case 'number':
                    return value ? '1' : '0';

                case 'custom':
                    const cust = Utils.parseBooleanCustomFormat(format.customFormat);

                    return value ? cust._true : cust._false;

                case 'Checkbox':
                default:
                    return valueText;
            }

        case ATTRIBUTE_TYPES.DATE:
        case ATTRIBUTE_TYPES.TIME:
        case ATTRIBUTE_TYPES.DATETIME:
            const res = convertDate(
                value,
                timePart,
                format.formatFront,
                dataType,
                onConverionErrorCallback,
                format.customFormat,
                format.use24hours,
                format.firstDayOfWeek,
                format.fiscalFirstMonth,
                format.useFiscalPrefix,
                format.fiscalYearDivider
            );

            return res || valueText;

        default:
            return valueText;
    }
}
